/**
 * ReportPdfDialog's settings and helpers: zoom choices and the remembered zoom,
 * download names, and the telemetry event for toolbar actions.
 */
import { trackEvent } from '../../utils/telemetry';
import { PAGE_FIT, PAGE_WIDTH } from './usePdfViewer';

/** Telemetry event for the toolbar actions. */
export const REPORT_VIEWER_EVENT = 'ReportViewerAction';
export type ReportViewerAction = 'print' | 'pdf' | 'xlsx' | 'docx' | 'csv' | 'download-pdf' | 'find';

/** Record which toolbar action was used. */
export function trackViewerAction(action: ReportViewerAction) {
  trackEvent(REPORT_VIEWER_EVENT, { action });
}

/** Zoom choices. */
export const ZOOM_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: PAGE_FIT, label: 'Whole Page' },
  { value: PAGE_WIDTH, label: 'Page Width' },
  ...[0.5, 0.75, 1, 1.25, 1.5, 2].map((v) => ({ value: String(v), label: `${v * 100}%` })),
];

/** The zoom a report opens at until the user picks another from the Zoom list. */
export const DEFAULT_ZOOM = PAGE_WIDTH;

/**
 * The zoom last picked from the Zoom list, per browser. ".v2": the first key
 * also held the zoom every report merely opened at (Whole Page), so it is
 * ignored rather than letting it hide the Page Width default.
 */
export const ZOOM_STORAGE_KEY = 'dietech.reportViewer.zoom.v2';
/** The first key, removed when the zoom is next read. */
export const LEGACY_ZOOM_STORAGE_KEY = 'dietech.reportViewer.zoom';

function isZoomValue(v: string): boolean {
  if (v === PAGE_FIT || v === PAGE_WIDTH) return true;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0.1 && n <= 10;
}

/** The zoom to open a report at: the user's last pick from the Zoom list, else Page Width. */
export function readSavedZoom(): string {
  try {
    window.localStorage.removeItem(LEGACY_ZOOM_STORAGE_KEY);
    const v = window.localStorage.getItem(ZOOM_STORAGE_KEY);
    if (v && isZoomValue(v)) return v;
  } catch { /* storage blocked: use the default */ }
  return DEFAULT_ZOOM;
}

/** Remember a zoom the user picked. Call it only for an explicit pick, not the zoom a report opened at. */
export function saveZoom(v: string) {
  try {
    if (isZoomValue(v)) window.localStorage.setItem(ZOOM_STORAGE_KEY, v);
  } catch { /* storage blocked: nothing to remember */ }
}

/** A download name from the report name: characters a file name can't hold are dropped. */
export function reportFileName(reportName: string | undefined, extension: string): string {
  // eslint-disable-next-line no-control-regex
  const base = (reportName ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]/g, '').trim().replace(/\.+$/, '');
  return `${base || 'Report'}.${extension}`;
}
